import { Col, Row } from 'antd';
import { reject } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz, Ic1, Ic2Prehled, Ic3neprima, Ic3prima, Ic4, Ic5, Ic6 } from 'types/amlz';

import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import isinRozcestnikApi, { IRozcestnikData } from 'services/akcionar/emise/rozcestnikISIN';
import C4osoby, { EEsmStav, IOsobyData } from 'services/spravaEmitenta/C4osoby';
import {
    amlzC1get,
    amlzC1send,
    amlzC2osobyGet,
    amlzC3neprimaGet,
    amlzC3neprimaSend,
    amlzC3primaGet,
    amlzC3primaSend,
    amlzC4Get,
    amlzC4Send,
    amlzC5Get,
    amlzC5Send,
    amlzC6Get,
    amlzC6Send,
    zalozitAmlzZadost
} from 'services/spravaEmitenta/amlzData';
import { EStavZadostAmlz } from 'services/spravaEmitenta/historickeZadosti';

import { EAmlzFormularSteps } from 'enums/amlz/amlzFormularSteps';
import { ENotificationType } from 'enums/notificationType';

import getLanguageObject from 'utils/getLanguageObject';

import C0 from './C0';
import C1 from './C1';
import C2 from './C2/C2';
import C3 from './C3/C3';
import C4 from './C4/C4';
import C5 from './C5/C5';
import C6 from './C6';
import DLTpotvrzeni from './DLTpotvrzeni';

interface IAmlzFormular {}

const AmlzFormular: React.FC<IAmlzFormular> = () => {
    const { amlz, token, cisStaty, NotificationComponent, language, profily } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const isLegalEntity = profily?.typprihlaseni === 'DLT'; // pravnicka osoba
    const [dltConsent, setDltConsent] = useState<boolean>(false);
    const [C4osobyResp, setC4osobyResp] = useState<IOsobyData>();
    const isNewAmlz = amlz && amlz.idzadosti ? false : true;
    const [currentStep, setCurrentStep] = useState<number>(
        amlz?.goToC6
            ? 6
            : amlz &&
                !(
                    isNewAmlz ||
                    amlz?.stav === EStavZadostAmlz.HOTOVO ||
                    amlz?.stav === EStavZadostAmlz.ZAMITNUTO ||
                    amlz?.stav === EStavZadostAmlz.ZNEPLATNENO ||
                    amlz?.stav === EStavZadostAmlz.HOTOVOIIS
                )
              ? 1
              : 0
    );
    const amlzFormatted =
        amlz && amlz.amlz
            ? {
                  ...amlz.amlz,
                  c4: {
                      ...amlz.amlz.c4,
                      subj: (amlz.amlz.c4?.subj || []).filter(e => !!e.stavesm)
                  }
              }
            : {
                  c2: {
                      count: 0,
                      order: 'id:desc',
                      limit: 0,
                      offset: 0,
                      osoby: []
                  }
              };
    const [nextStepQueued, setNextStepQueued] = useState<boolean>(false);
    const [formData, setFormData] = useState<IAmlz['amlz']>(amlzFormatted);
    const [submitData, setSubmitData] = useState<IAmlz['amlz']>();
    const [loading, setLoading] = useState<boolean>(true);
    const [leiError, setLeiError] = useState<boolean>(false);
    const [rozcestnikData, setRozcestnikData] = useState<IRozcestnikData['spolecnost'] | undefined>();
    const [prevTo3b, setPrevTo3b] = useState<boolean>(false);
    const [C4apiFinished, setC4apiFinished] = useState<boolean>(false);
    const [c3lastStep, setC3lastStep] = useState(false);
    const [isC6edit, setIsC6edit] = useState<EAmlzFormularSteps | ''>('');
    const idZadosti = amlz?.idzadosti || 0;
    const [saving, setSaving] = useState<boolean>(false);
    const [refetchC2, setRefetchC2] = useState<boolean>(false);
    // const [saveModalMessage, setSaveModalMessage] = useState<string>(''); zakomentovany kod moze byt v buducnosti pouzity na ukladanie ziadosti (zatial nepotrebny)

    const next = () => {
        isC6edit ? setCurrentStep(6) : setTimeout(() => setCurrentStep(currentStep + 1), 0);
    };

    const prev = () => {
        currentStep === 4 && setPrevTo3b(true);
        setCurrentStep(currentStep - 1);
    };

    const queueNextStep = () => {
        setNextStepQueued(true);
    };

    const SubmitFunc = <T extends keyof IAmlz['amlz']>(key: T, values: IAmlz['amlz'][T]) => {
        setSubmitData({
            [key]: values
        });
    };

    useEffect(() => {
        if (amlz?.goToC6 && idZadosti) {
            setLoading(true);
            Promise.all([
                amlzC1get(idZadosti),
                amlzC2osobyGet(idZadosti),
                amlzC3neprimaGet(idZadosti),
                amlzC3primaGet(idZadosti),
                amlzC4Get(idZadosti),
                amlzC5Get(idZadosti),
                amlzC6Get(idZadosti)
            ])
                .then(resp => {
                    setFormData(pv => {
                        return {
                            ...pv,
                            c1: resp[0],
                            c2: resp[1],
                            c3neprima: resp[2],
                            c3prima: resp[3],
                            c4: resp[4],
                            c5: resp[5],
                            c6: resp[6]
                        };
                    });
                })
                .finally(() => {
                    setLoading(false);
                    setCurrentStep(6);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amlz?.goToC6, idZadosti]);

    useEffect(() => {
        if (amlz?.goToC2Osoba && idZadosti) {
            setCurrentStep(2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idZadosti, amlz?.goToC2Osoba]);

    useEffect(() => {
        if (!rozcestnikData && !amlz?.goToC6 && isLegalEntity && isNewAmlz ? dltConsent : true) {
            if (
                isNewAmlz ||
                amlz?.stav === EStavZadostAmlz.HOTOVO ||
                amlz?.stav === EStavZadostAmlz.ZAMITNUTO ||
                amlz?.stav === EStavZadostAmlz.ZNEPLATNENO ||
                amlz?.stav === EStavZadostAmlz.HOTOVOIIS
            ) {
                setLoading(true);
                zalozitAmlzZadost(isLegalEntity)
                    .then(resp => {
                        if (resp.stav === 'ZALOZENO') {
                            setLevel('amlz', {
                                ...resp,
                                ...(amlz?.idPoslednejHotovejZadosti && {
                                    idPoslednejHotovejZadosti: amlz?.idPoslednejHotovejZadosti
                                })
                            } as unknown as IAmlz);
                            setCurrentStep(amlz?.idPoslednejHotovejZadosti ? 1 : 0);
                        } else {
                            NotificationComponent({
                                title:
                                    getLanguageObject(language)['NELZE_POKRACOVAT_' + resp.stav] +
                                        ' ' +
                                        resp.uzivatel || '',
                                type: ENotificationType.ERROR
                            });
                            navigateWorkaround(-1);
                        }
                    })
                    .catch(() => navigateWorkaround(-1))
                    .finally(() => setLoading(false));
            } else if (!isNewAmlz && formData?.c1?.idmb) {
                setLoading(true);
                isinRozcestnikApi({
                    idmb: formData.c1.idmb || '',
                    stat:
                        typeof formData.c1.stat === 'string'
                            ? formData.c1.stat
                            : typeof formData.c1.stat === 'object' && formData.c1.stat.length
                              ? formData.c1.stat[0]
                              : formData.c1.kost || ''
                })
                    .then(rezp => setRozcestnikData(rezp.spolecnost))
                    .finally(() => setLoading(false));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.c1?.idmb, dltConsent]);

    useEffect(() => {
        // formData sa setne na data z C0
        if (rozcestnikData && rozcestnikData.nazev) {
            if (isNewAmlz) {
                const newData = {
                    c1: {
                        ...(rozcestnikData?.adma && {
                            adma: rozcestnikData.adma
                        }),
                        ...(rozcestnikData?.idmb && {
                            idmb: rozcestnikData.idmb
                        }),
                        ...(rozcestnikData?.kodpravniformy && {
                            kodpravniformy: rozcestnikData.kodpravniformy
                        }),
                        ...(rozcestnikData?.kost && {
                            kost: rozcestnikData.kost
                        }),
                        ...(rozcestnikData?.lei && {
                            lei: rozcestnikData.lei
                        }),
                        ...(rozcestnikData?.mema && {
                            mema: rozcestnikData.mema
                        }),
                        ...(rozcestnikData?.nazev && {
                            nazev: rozcestnikData.nazev
                        }),
                        ...(rozcestnikData?.pravniforma && {
                            pravniforma: rozcestnikData.pravniforma
                        }),
                        ...(rozcestnikData?.pscm && {
                            pscm: rozcestnikData.pscm
                        }),
                        ...(rozcestnikData?.stat && {
                            stat: [rozcestnikData.stat]
                        })
                    },
                    c2: {
                        count: 0,
                        order: 'id:desc',
                        limit: 0,
                        offset: 0,
                        osoby: []
                    },
                    c3neprima: {},
                    c3prima: {},
                    c4: {},
                    c5: {},
                    c6: {}
                };
                setFormData(newData);
                SubmitFunc('c1', newData.c1);
            } else {
                setFormData(pv => {
                    const newData = {
                        ...pv,
                        c1: {
                            //   ...(Object.prototype.hasOwnProperty.call(pv, 'c1') && pv.c1),
                            ...pv?.c1,
                            ...(rozcestnikData?.adma && {
                                adma: rozcestnikData.adma
                            }),
                            ...(rozcestnikData?.idmb && {
                                idmb: rozcestnikData.idmb
                            }),
                            ...(rozcestnikData?.kodpravniformy && {
                                kodpravniformy: rozcestnikData.kodpravniformy
                            }),
                            ...(rozcestnikData?.kost && {
                                kost: rozcestnikData.kost
                            }),
                            ...(rozcestnikData?.lei && {
                                lei: rozcestnikData.lei
                            }),
                            ...(rozcestnikData?.mema && {
                                mema: rozcestnikData.mema
                            }),
                            ...(rozcestnikData?.nazev && {
                                nazev: rozcestnikData.nazev
                            }),
                            ...(rozcestnikData?.pravniforma && {
                                pravniforma: rozcestnikData.pravniforma
                            }),
                            ...(rozcestnikData?.pscm && {
                                pscm: rozcestnikData.pscm
                            }),
                            ...(rozcestnikData?.stat && {
                                stat: [rozcestnikData.stat]
                            })
                        }
                    };
                    SubmitFunc('c1', newData.c1);
                    return newData;
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rozcestnikData]);

    useEffect(() => {
        if (amlz && !formData?.c2) {
            setFormData(amlzFormatted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amlz]);

    useEffect(() => {
        // / AMLZ PUT
        if (submitData) {
            // pri c3 su 2 stranky musel som to spravit takto skaredo :(
            if (currentStep === 3) {
                if (submitData.c3prima) {
                    amlzC3primaSend(idZadosti, submitData?.c3prima!, saving ? 'PUT' : 'POST')
                        .then(() => {
                            if (nextStepQueued) {
                                next();
                                setNextStepQueued(false);
                            }
                        })
                        .finally(() => {
                            setSaving(false);
                            setFormData(pv => {
                                return {
                                    ...pv,
                                    c3prima: submitData.c3prima as Ic3prima
                                };
                            });
                            setSubmitData(undefined);
                        });
                } else {
                    amlzC3neprimaSend(idZadosti, submitData?.c3neprima!, saving ? 'PUT' : 'POST')
                        .then(() => {
                            if (nextStepQueued) {
                                next();
                                setNextStepQueued(false);
                            }
                        })
                        .finally(() => {
                            setSaving(false);
                            setFormData(pv => {
                                return {
                                    ...pv,
                                    c3neprima: submitData.c3neprima as Ic3neprima
                                };
                            });
                            setSubmitData(undefined);
                        });

                    /*  .then(() => {
                                saving && setSaveModalMessage('ULOZENI_DAT_200');
                            })
                            .catch(() => saving && setSaveModalMessage('CHYBA_ULOZENI_DAT'))
                            .finally(() => setSaving(false));*/
                }
            } else {
                const dataToSend =
                    currentStep === 1
                        ? submitData?.c1
                        : currentStep === 2
                          ? submitData?.c2
                          : currentStep === 4
                            ? submitData?.c4
                            : currentStep === 5
                              ? submitData?.c5
                              : submitData?.c6;

                if (dataToSend) {
                    if (currentStep === 2) {
                        setFormData(pv => {
                            return {
                                ...pv,
                                c2: dataToSend as Ic2Prehled
                            };
                        });
                    } else {
                        currentStep !== 2 && setLoading(true);
                        (currentStep === 1
                            ? amlzC1send(idZadosti, dataToSend as Ic1, saving ? 'PUT' : 'POST')
                            : currentStep === 4
                              ? amlzC4Send(idZadosti, dataToSend as Ic4, saving ? 'PUT' : 'POST')
                              : currentStep === 5
                                ? amlzC5Send(idZadosti, dataToSend as Ic5, saving ? 'PUT' : 'POST')
                                : amlzC6Send(idZadosti, dataToSend as Ic6, saving ? 'PUT' : 'POST')
                        )
                            .then(() => {
                                if (nextStepQueued) {
                                    next();
                                    setNextStepQueued(false);
                                }
                            })
                            .finally(() => {
                                setLoading(false);
                                setSubmitData(undefined);
                                setSaving(false);
                            });
                    }
                    setFormData(pv => {
                        return {
                            ...pv,
                            ...(currentStep === 1
                                ? {
                                      c1: dataToSend as Ic1
                                  }
                                : currentStep === 4
                                  ? {
                                        c4: dataToSend as Ic4
                                    }
                                  : currentStep === 5
                                    ? {
                                          c5: dataToSend as Ic5
                                      }
                                    : {
                                          c6: dataToSend as Ic6
                                      })
                        };
                    });
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any

                    /*    .then(() => {
                        saving && setSaveModalMessage('ULOZENI_DAT_200');
                    })
                    .catch(() => saving && setSaveModalMessage('CHYBA_ULOZENI_DAT'))
                    .finally(() => setSaving(false));*/
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitData]);

    const getC4OsobyESM = () =>
        C4osoby(formData?.c1?.idmb!).then(resp => {
            const formatedResp = {
                datumnacteni: resp.datumnacteni,
                osoby: resp.osoby.map(e => {
                    const country = cisStaty?.filter(u => e.stat === u.labelczlong || e.stat === u.labelcz) || [];
                    const newData = {
                        ...e,
                        stavesm: EEsmStav.ESMDOPL,
                        ...(e.datumnarozeni && {
                            datumnar: e.datumnarozeni
                        }),
                        ...(e.ulice && {
                            adma: e.ulice
                        }),
                        ...(e.mesto && {
                            mema: e.mesto
                        }),
                        ...(e.psc && {
                            pscm: e.psc
                        }),
                        ...(e.stat && {
                            kost: country.length > 0 ? country[0].value : e.stat
                        })
                    };
                    delete newData.datumnarozeni;
                    delete newData.mesto;
                    delete newData.psc;
                    delete newData.ulice;
                    delete newData.stat;
                    return newData;
                })
            };
            setC4osobyResp(formatedResp);
        });

    /** Obejde TypeScript chybu "TS2349" (nekřičel přímo editor, ale až build) */
    const getCData = () => {
        return new Promise(resolve => {
            currentStep === 1
                ? amlzC1get(idZadosti)
                      .then(data => resolve(data))
                      .catch(() => {
                          reject('');
                          setLoading(false);
                      })
                : currentStep === 2
                  ? amlzC2osobyGet(idZadosti)
                        .then(data => resolve(data))
                        .catch(() => {
                            reject('');
                            setLoading(false);
                        })
                  : currentStep === 3
                    ? c3lastStep
                        ? amlzC3neprimaGet(idZadosti)
                              .then(data => resolve(data))
                              .catch(() => {
                                  resolve({
                                      subj: []
                                  });
                                  setLoading(false);
                              })
                        : amlzC3primaGet(idZadosti)
                              .then(data => resolve(data))
                              .catch(() => {
                                  resolve({
                                      subj: []
                                  });
                                  setLoading(false);
                              })
                    : currentStep === 4
                      ? amlzC4Get(idZadosti)
                            .then(data => {
                                getC4OsobyESM()
                                    .then(() => {
                                        resolve(data);
                                    })
                                    .catch(() => {
                                        setLoading(false);
                                        resolve(data);
                                    });
                            })
                            .catch(() => {
                                setLoading(false);
                                reject('');
                            })
                      : currentStep === 5
                        ? amlzC5Get(idZadosti)
                              .then(data => resolve(data))
                              .catch(() => {
                                  reject('');
                                  setLoading(false);
                              })
                        : amlzC6Get(idZadosti)
                              .then(data => resolve(data))
                              .catch(() => {
                                  reject('');
                                  setLoading(false);
                              });
        });
    };

    useEffect(() => {
        if ((!saving || refetchC2) && currentStep > 0) {
            const updateC2 = currentStep === 2 && (formData?.c2?.osoby || []).length && refetchC2;
            const canUpdate = (currentStep === 2 && updateC2) || !formData?.c2?.osoby.length || currentStep !== 2;
            (canUpdate || saving) && setLoading(true);
            currentStep !== 4 && C4apiFinished && setC4apiFinished(false);
            // (currentStep === 1
            //     ? amlzC1get(idZadosti)
            //     : currentStep === 2
            //     ? amlzC2osobyGet(idZadosti)
            //     : currentStep === 3
            //     ? prevTo3b
            //         ? amlzC3neprimaGet(idZadosti)
            //         : amlzC3neprimaGet(idZadosti)
            //     : currentStep === 4
            //     ? amlzC4Get(idZadosti)
            //     : currentStep === 5
            //     ? amlzC5Get(idZadosti)
            //     : amlzC6Get(idZadosti)
            // )

            (canUpdate || saving) &&
                getCData()
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .then((resp: any) => {
                        currentStep === 4 && setTimeout(() => setC4apiFinished(true), 0);
                        setFormData(pv => {
                            return {
                                ...pv,
                                ...(currentStep === 1
                                    ? {
                                          c1: resp
                                      }
                                    : currentStep === 2
                                      ? {
                                            c2: resp
                                        }
                                      : currentStep === 3
                                        ? c3lastStep
                                            ? {
                                                  c3neprima: resp
                                              }
                                            : {
                                                  c3prima: resp
                                              }
                                        : currentStep === 4
                                          ? {
                                                c4: resp
                                            }
                                          : currentStep === 5
                                            ? {
                                                  c5: resp
                                              }
                                            : {
                                                  c6: resp
                                              })
                            };
                        });
                    })
                    .finally(() => {
                        setLoading(false);
                        setRefetchC2(false);
                    });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, refetchC2, c3lastStep]);

    const steps = [
        {
            title: isLegalEntity ? '' : 'ZAKLADNI_UDAJE_O_EMITENTOVI',
            content: (
                <C0
                    isLegalEntity={isLegalEntity}
                    countries={cisStaty}
                    nextStep={next}
                    setFormData={setFormData}
                    setRozcestnikData={setRozcestnikData}
                />
            )
        },
        {
            title: isLegalEntity ? 'FORMULARE_AMLZ_C1_TITLE_LEGALENTITY' : 'FORMULARE_AMLZ_C1_TITLE',
            content: (
                <C1
                    isLegalEntity={isLegalEntity}
                    rozcestnikData={rozcestnikData}
                    C1data={formData?.c1}
                    countries={cisStaty}
                    nextStep={queueNextStep}
                    SubmitFunc={SubmitFunc}
                    leiError={leiError}
                    isC6edit={isC6edit}
                    setLeiError={setLeiError}
                    setSaving={setSaving}
                />
            )
        },
        {
            title: isLegalEntity ? 'FORMULARE_AMLZ_C2_TITLE_LEGALENTITY' : 'FORMULARE_AMLZ_C2_TITLE',
            content: (
                <C2
                    isLegalEntity={isLegalEntity}
                    C2data={formData?.c2}
                    countries={cisStaty}
                    auth={token}
                    nextStep={next}
                    prevStep={prev}
                    SubmitFunc={SubmitFunc}
                    isC6edit={isC6edit}
                    idZadosti={idZadosti}
                    setRefetchC2={setRefetchC2}
                />
            )
        },
        {
            title: '',
            content: (
                <C3
                    isLegalEntity={isLegalEntity}
                    C3dataNeprima={formData?.c3neprima}
                    C3dataPrima={formData?.c3prima}
                    c3lastStep={c3lastStep}
                    setC3lastStep={setC3lastStep}
                    countries={cisStaty}
                    nextStep={queueNextStep}
                    prevStep={prev}
                    SubmitFunc={SubmitFunc}
                    isC6edit={isC6edit}
                    prevTo3b={prevTo3b}
                    //  saving={saving}
                    setPrevTo3b={setPrevTo3b}
                    setSaving={setSaving}
                />
            )
        },
        {
            title: '',
            content: (
                <C4
                    isLegalEntity={isLegalEntity}
                    C4apiFinished={C4apiFinished}
                    C4data={formData?.c4}
                    osobyResp={C4osobyResp}
                    c1kodpravniformy={formData?.c1?.kodpravniformy || ''}
                    countries={cisStaty}
                    nextStep={queueNextStep}
                    prevStep={prev}
                    SubmitFunc={SubmitFunc}
                    // saving={saving}
                    isC6edit={isC6edit}
                    setSaving={setSaving}
                    idZadosti={idZadosti}
                />
            )
        },
        {
            title: isLegalEntity ? 'FORMULARE_AMLZ_C5_TITLE_LEGALENTITY' : 'FORMULARE_AMLZ_C5_TITLE',
            content: (
                <C5
                    isLegalEntity={isLegalEntity}
                    C5data={formData?.c5}
                    countries={cisStaty}
                    nextStep={queueNextStep}
                    prevStep={prev}
                    SubmitFunc={SubmitFunc}
                    //   saving={saving}
                    idZadosti={idZadosti}
                    isC6edit={isC6edit}
                    setSaving={setSaving}
                />
            )
        },
        {
            title: '',
            content: (
                <C6
                    isLegalEntity={isLegalEntity}
                    formData={formData}
                    countries={cisStaty}
                    prevStep={prev}
                    setCurrentStep={setCurrentStep}
                    setIsC6edit={setIsC6edit}
                    //  saving={saving}
                    idZadosti={idZadosti}
                />
            )
        }
    ];

    return isLegalEntity && !dltConsent && isNewAmlz ? (
        <DLTpotvrzeni setDltConsent={setDltConsent} />
    ) : (
        <LoadingWrapper loading={loading}>
            <Title
                level={3}
                text={isLegalEntity ? 'ZAKLADNI_UDAJE_O_PRAVNICKE_OSOBE' : 'EMP_AMLZ_DLAZDICE_NADPIS_DOTAZNIK'}
            />
            {/* currentStep > 0 && (
                    <SaveButtonComponent
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            zIndex: 200
                        }}
                        onClick={() => {
                            setSaving(true);
                        }}
                        modalText={saveModalMessage}
                        onModalClose={() => setSaveModalMessage('')}
                    />
                    )*/}
            {currentStep > 0 && <Title level={4} text={steps[currentStep].title} />}
            {(currentStep === 0 || rozcestnikData || amlz?.goToC6) && (
                <Row gutter={8}>
                    <Col span={24}>{steps[currentStep].content}</Col>
                </Row>
            )}
        </LoadingWrapper>
    );
};

export default AmlzFormular;
